import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, Plus, Trash2, ArrowUp, ArrowDown, Zap } from 'lucide-react';

// Fallback templates used only until the API responds so the admin never sees
// an empty list on first paint.
const FALLBACK_TEMPLATES = [
  'દસ્તાવેજો પ્રાપ્ત થયા',
  'ચકાસણી ચાલુ છે',
];

export default function SiteSettings() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [siteName, setSiteName] = useState('કાગજબુક ઓનલાઈન');
  const [templates, setTemplates] = useState(FALLBACK_TEMPLATES);
  const [newTemplate, setNewTemplate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [API_URL]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      if (res.data?.site_name) setSiteName(res.data.site_name);
      if (Array.isArray(res.data?.remark_templates)) setTemplates(res.data.remark_templates);
    } catch (err) {
      console.error('Failed to fetch settings');
    }
  };

  const updateTemplate = (idx, value) => {
    setTemplates(prev => prev.map((t, i) => (i === idx ? value : t)));
  };

  const removeTemplate = (idx) => {
    setTemplates(prev => prev.filter((_, i) => i !== idx));
  };

  const moveTemplate = (idx, delta) => {
    setTemplates(prev => {
      const nextIdx = idx + delta;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
      return next;
    });
  };

  const addTemplate = () => {
    const trimmed = newTemplate.trim();
    if (!trimmed) return;
    if (templates.length >= 20) {
      toast.error('વધુમાં વધુ 20 ટેમ્પ્લેટ્સ (Max 20 templates)');
      return;
    }
    setTemplates(prev => [...prev, trimmed]);
    setNewTemplate('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const cleaned = templates.map(t => (t || '').trim()).filter(Boolean);
      await axios.put(
        `${API_URL}/api/settings`,
        { site_name: siteName, remark_templates: cleaned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(cleaned);
      toast.success('Site Settings saved successfully');
      window.dispatchEvent(new CustomEvent('siteSettingsUpdated', { detail: { site_name: siteName, remark_templates: cleaned } }));
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save settings';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <h3 className="text-lg font-bold mb-6 text-[#172554] flex items-center gap-2 border-b pb-4">
        <SettingsIcon className="w-5 h-5 text-gray-500" /> Site Settings
      </h3>

      <form onSubmit={handleSave} className="space-y-8">
        <div className="max-w-md">
          <label className="block text-sm font-bold text-gray-700 mb-2">Portal Name (Gujarati Header Title)</label>
          <input
            type="text" required
            className="w-full border border-gray-300 rounded-lg p-3 text-base outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gujarati"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="e.g. કાગજબુક ઓનલાઈન"
            data-testid="settings-site-name-input"
          />
          <p className="text-xs text-gray-500 mt-2">This name appears in the main header across all public pages.</p>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-[#F5B800]" />
            <label className="text-sm font-bold text-gray-700">ઝડપી રીમાર્ક ટેમ્પ્લેટ્સ (Quick Remark Templates)</label>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            ટેમ્પ્લેટ્સ Booking Management ના રીમાર્ક ⚡ પોપઓવરમાં દેખાય છે. ઉમેરો, સંપાદિત કરો અથવા ક્રમ બદલો.
          </p>

          <div className="space-y-2 max-w-2xl">
            {templates.length === 0 && (
              <div className="text-xs italic text-gray-400 py-2">કોઈ ટેમ્પ્લેટ્સ નથી. પ્રથમ ઉમેરો.</div>
            )}
            {templates.map((tpl, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-2" data-testid={`settings-template-row-${idx}`}>
                <span className="text-xs font-bold text-gray-400 w-5 text-center">{idx + 1}</span>
                <input
                  type="text"
                  value={tpl}
                  onChange={(e) => updateTemplate(idx, e.target.value)}
                  className="flex-1 border border-gray-200 bg-white rounded-md p-2 text-sm text-[#172554] text-gujarati outline-none focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800]"
                  placeholder="Gujarati remark..."
                  data-testid={`settings-template-input-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => moveTemplate(idx, -1)}
                  disabled={idx === 0}
                  className="p-1.5 rounded-md border border-gray-200 bg-white text-[#172554] hover:bg-[#FEF9E7] hover:border-[#F5B800] disabled:opacity-30 disabled:hover:bg-white disabled:hover:border-gray-200"
                  title="Move up"
                  aria-label="Move up"
                  data-testid={`settings-template-up-${idx}`}
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => moveTemplate(idx, 1)}
                  disabled={idx === templates.length - 1}
                  className="p-1.5 rounded-md border border-gray-200 bg-white text-[#172554] hover:bg-[#FEF9E7] hover:border-[#F5B800] disabled:opacity-30 disabled:hover:bg-white disabled:hover:border-gray-200"
                  title="Move down"
                  aria-label="Move down"
                  data-testid={`settings-template-down-${idx}`}
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => removeTemplate(idx)}
                  className="p-1.5 rounded-md border border-red-100 bg-white text-red-500 hover:bg-red-50 hover:border-red-300"
                  title="Delete"
                  aria-label="Delete"
                  data-testid={`settings-template-delete-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center gap-2 max-w-2xl">
            <input
              type="text"
              value={newTemplate}
              onChange={(e) => setNewTemplate(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTemplate(); } }}
              placeholder="નવો ટેમ્પ્લેટ લખો... (New template)"
              className="flex-1 border border-gray-300 rounded-lg p-2.5 text-sm text-gujarati outline-none focus:border-[#F5B800] focus:ring-1 focus:ring-[#F5B800]"
              data-testid="settings-template-new-input"
            />
            <button
              type="button"
              onClick={addTemplate}
              disabled={!newTemplate.trim() || templates.length >= 20}
              className="bg-[#F5B800] hover:bg-[#E5AE00] disabled:opacity-50 disabled:cursor-not-allowed text-[#172554] font-bold py-2.5 px-4 rounded-lg flex items-center gap-1.5 text-sm"
              data-testid="settings-template-add-btn"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">{templates.length} / 20 ટેમ્પ્લેટ્સ</p>
        </div>

        <button
          type="submit"
          disabled={isSaving}
          className="bg-[#172554] text-white py-2.5 px-6 rounded-lg font-bold flex items-center gap-2 hover:bg-blue-900 transition-colors disabled:opacity-50"
          data-testid="settings-save-btn"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}
